/**
 * Input component
 * Reusable form input with validation states
 */

import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
} from "react";
import styles from "./Input.module.css";

/**
 * Input component props
 */
export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /** Input label */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Input size */
  inputSize?: "small" | "medium" | "large";
  /** Left icon or element */
  leftElement?: ReactNode;
  /** Right icon or element */
  rightElement?: ReactNode;
  /** Full width input */
  fullWidth?: boolean;
}

/**
 * Reusable input component with accessibility support
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      inputSize = "medium",
      leftElement,
      rightElement,
      fullWidth = false,
      className,
      id,
      disabled,
      required,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const describedBy =
      [errorId, helperId].filter(Boolean).join(" ") || undefined;

    const containerClasses = [
      styles.container,
      fullWidth ? styles.fullWidth : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const inputWrapperClasses = [
      styles.inputWrapper,
      styles[inputSize],
      error ? styles.hasError : "",
      disabled ? styles.disabled : "",
      leftElement ? styles.hasLeftElement : "",
      rightElement ? styles.hasRightElement : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                {" "}
                *
              </span>
            )}
          </label>
        )}
        <div className={inputWrapperClasses}>
          {leftElement && (
            <span className={styles.leftElement} aria-hidden="true">
              {leftElement}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={styles.input}
            disabled={disabled}
            required={required}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            {...props}
          />
          {rightElement && (
            <span className={styles.rightElement} aria-hidden="true">
              {rightElement}
            </span>
          )}
        </div>
        {error && (
          <span id={errorId} className={styles.error} role="alert">
            {error}
          </span>
        )}
        {helperText && !error && (
          <span id={helperId} className={styles.helperText}>
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
